/**
 * Navigation reducer.
 * @module reducers/navigation/navigation
 */

import {
  flattenToAppURL,
  getBaseUrl,
  hasApiExpander,
} from '@plone/volto/helpers';

import {
  GET_CONTENT,
  GET_NAVIGATION,
} from '@plone/volto/constants/ActionTypes';

const initialState = {
  error: null,
  items: [],
  loaded: false,
  loading: false,
};

/**
 * Recursive function that process the items returned by the navigation
 * endpoint
 *
 * Além do formato base, propaga os metadados do behavior `menu_meta`
 * (`icon`/`teaser`/`highlight`/`headerLink`) e descarta os itens marcados como `hidden`:
 * eles saem do menu sem sair da navegação (breadcrumb, mapa do site).
 *
 * O estado inteiro é serializado no HTML do SSR, então só entra aqui o que o
 * menu desenha — a `description` do @navigation não tem consumidor e ficou
 * de fora (o backend nem a envia mais).
 *
 * Quando um item é marcado como `hidden` (ex: default page), seus filhos
 * são promovidos para o nível do pai, evitando que fiquem órfãos.
 *
 * @function getRecursiveItems
 * @param {array} items The items inside a navigation response.
 * @returns {*} The navigation items object (recursive)
 */
function getRecursiveItems(items) {
  const result = [];

  (items || []).forEach((item) => {
    // Processa os filhos recursivamente
    const processedChildren = item.items ? getRecursiveItems(item.items) : [];

    if (item.hidden) {
      // Se o item é hidden (ex: default page), promove seus filhos
      // para o nível do pai, ao invés de descartar tudo
      result.push(...processedChildren);
    } else {
      // Se o item não é hidden, mantém normalmente
      result.push({
        title: item.title,
        url: flattenToAppURL(item['@id']),
        href: item.href,
        type: item.type,
        icon: item.icon,
        teaser: item.teaser,
        highlight: item.highlight,
        ...(item.headerLink && {
          headerLink: flattenToAppURL(item.headerLink),
        }),
        ...(processedChildren.length > 0 && { items: processedChildren }),
      });
    }
  });

  return result;
}

/**
 * Navigation reducer.
 * @function navigation
 * @param {Object} state Current state.
 * @param {Object} action Action to be handled.
 * @returns {Object} New state.
 */
export default function navigation(state = initialState, action = {}) {
  let hasExpander;
  switch (action.type) {
    case `${GET_NAVIGATION}_PENDING`:
      return {
        ...state,
        error: null,
        loaded: false,
        loading: true,
      };
    case `${GET_CONTENT}_SUCCESS`:
      hasExpander = hasApiExpander(
        'navigation',
        getBaseUrl(flattenToAppURL(action.result['@id'])),
      );
      if (hasExpander && !action.subrequest) {
        return {
          ...state,
          error: null,
          items: getRecursiveItems(
            action.result['@components'].navigation.items,
          ),
          loaded: true,
          loading: false,
        };
      }
      return state;
    case `${GET_NAVIGATION}_SUCCESS`:
      hasExpander = hasApiExpander(
        'navigation',
        getBaseUrl(flattenToAppURL(action.result['@id'])),
      );
      if (!hasExpander) {
        return {
          ...state,
          error: null,
          items: getRecursiveItems(action.result.items),
          loaded: true,
          loading: false,
        };
      }
      return state;
    case `${GET_NAVIGATION}_FAIL`:
      return {
        ...state,
        error: action.error,
        items: [],
        loaded: false,
        loading: false,
      };
    default:
      return state;
  }
}
